/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { useState, useEffect } from 'react';
import { sprintsApi, milestonesApi, projectsApi, tasksApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useAppConfig } from '../contexts/ConfigContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus, Flag, Lightning, CalendarBlank, Play, CheckCircle, Warning, Clock,
  PencilSimple, Trash, ArrowRight, X, ListChecks, LinkSimple, Bell
} from '@phosphor-icons/react';
import { TrendingDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, differenceInDays, isPast, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { formatDate, formatTimeHundredths, getLabel, getBadgeClass } from '../lib/utils';
import { toast } from 'sonner';

export default function SprintsMilestonesPage() {
  const { getList } = useAppConfig();
  const SPRINT_STATUSES = getList('sprint_status', [{value:'planned',label:'Planifie'},{value:'active',label:'Actif'},{value:'completed',label:'Termine'}]);
  const MILESTONE_STATUSES = getList('milestone_status', [{value:'pending',label:'En attente'},{value:'achieved',label:'Atteint'},{value:'delayed',label:'En retard'}]);
  const { hasPermission } = useAuth();
  const [sprints, setSprints] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectFilter, setProjectFilter] = useState('all');
  const [burndownSprint, setBurndownSprint] = useState(null);
  const [burndownData, setBurndownData] = useState(null);

  // Create dialogs
  const [isCreateSprintOpen, setIsCreateSprintOpen] = useState(false);
  const [isCreateMilestoneOpen, setIsCreateMilestoneOpen] = useState(false);
  // Edit dialogs
  const [editingSprint, setEditingSprint] = useState(null);
  const [editingMilestone, setEditingMilestone] = useState(null);
  // Sprint tasks
  const [viewingSprintTasks, setViewingSprintTasks] = useState(null);
  const [sprintTasks, setSprintTasks] = useState([]);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [milestoneTasks, setMilestoneTasks] = useState([]);

  const [newSprint, setNewSprint] = useState({
    project_id: '', name: '', description: '',
    start_date: new Date(), end_date: addDays(new Date(), 14), capacity: '', working_days_only: true,
  });
  const [newMilestone, setNewMilestone] = useState({
    project_id: '', name: '', description: '', target_date: new Date(), task_id: '', subtask_id: '', criticality: 'medium', reminder_value: 1, reminder_unit: 'days', web_link: '',
  });

  useEffect(() => { fetchData(); }, [projectFilter]);

  useEffect(() => {
    const pid = newMilestone.project_id || editingMilestone?.project_id;
    if (!pid) { setMilestoneTasks([]); return; }
    tasksApi.getAll({ project_id: pid }).then(r => setMilestoneTasks(r.data || [])).catch(() => setMilestoneTasks([]));
  }, [newMilestone.project_id, editingMilestone?.project_id]);

  const fetchData = async () => {
    try {
      const [spRes, msRes, prRes] = await Promise.all([
        sprintsApi.getAll({ project_id: projectFilter !== 'all' ? projectFilter : undefined }),
        milestonesApi.getAll({ project_id: projectFilter !== 'all' ? projectFilter : undefined }),
        projectsApi.getAll(),
      ]);
      setSprints(spRes.data);
      setMilestones(msRes.data);
      setProjects(prRes.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  const loadBurndown = async (sprint) => {
    setBurndownSprint(sprint);
    try {
      const res = await sprintsApi.burndown(sprint.id);
      const data = res.data || res;
      // Merge ideal and actual into single chart data
      const chartData = data.ideal.map((point, i) => ({
        date: point.date,
        ideal: point.remaining,
        actual: data.actual[i] ? data.actual[i].remaining : null,
      }));
      setBurndownData({ ...data, chartData });
    } catch { toast.error('Erreur burndown'); }
  };

  // ========== SPRINTS ==========
  const handleCreateSprint = async () => {
    try {
      await sprintsApi.create({
        ...newSprint,
        start_date: newSprint.start_date.toISOString(),
        end_date: newSprint.end_date.toISOString(),
        capacity: newSprint.capacity ? parseFloat(newSprint.capacity) : null,
      });
      toast.success('Sprint cree');
      setIsCreateSprintOpen(false);
      setNewSprint({ project_id: '', name: '', description: '', start_date: new Date(), end_date: addDays(new Date(), 14), capacity: '', working_days_only: true });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const openEditSprint = (sprint) => {
    setEditingSprint({
      ...sprint,
      start_date: new Date(sprint.start_date),
      end_date: new Date(sprint.end_date),
      capacity: sprint.capacity ?? '',
    });
  };

  const handleUpdateSprint = async () => {
    try {
      await sprintsApi.update(editingSprint.id, {
        name: editingSprint.name,
        description: editingSprint.description,
        start_date: editingSprint.start_date.toISOString(),
        end_date: editingSprint.end_date.toISOString(),
        capacity: editingSprint.capacity ? parseFloat(editingSprint.capacity) : null,
        working_days_only: editingSprint.working_days_only !== false,
        status: editingSprint.status,
      });
      toast.success('Sprint mis a jour');
      setEditingSprint(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleSprintStatus = async (id, status) => {
    try {
      await sprintsApi.update(id, { status });
      toast.success('Statut mis a jour');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  // ========== SPRINT TASKS ==========
  const openSprintTasks = async (sprint) => {
    setViewingSprintTasks(sprint);
    try {
      const [tasksRes, allTasksRes] = await Promise.all([
        sprintsApi.getTasks(sprint.id),
        tasksApi.getAll({ project_id: sprint.project_id }),
      ]);
      setSprintTasks(tasksRes.data);
      const sprintTaskIds = tasksRes.data.map(t => t.id);
      setAvailableTasks(allTasksRes.data.filter(t => !sprintTaskIds.includes(t.id) && (!t.sprint_id || t.sprint_id === sprint.id)));
    } catch { toast.error('Erreur chargement taches'); }
  };

  const handleAddTaskToSprint = async (taskId) => {
    try {
      await sprintsApi.addTask(viewingSprintTasks.id, taskId);
      toast.success('Tache ajoutee');
      openSprintTasks(viewingSprintTasks);
    } catch { toast.error('Erreur'); }
  };

  const handleRemoveTaskFromSprint = async (taskId) => {
    try {
      await sprintsApi.removeTask(viewingSprintTasks.id, taskId);
      toast.success('Tache retiree');
      openSprintTasks(viewingSprintTasks);
    } catch { toast.error('Erreur'); }
  };

  // ========== MILESTONES ==========
  const handleCreateMilestone = async () => {
    try {
      await milestonesApi.create({
        ...newMilestone,
        target_date: newMilestone.target_date.toISOString(),
        web_links: newMilestone.web_link ? [{ label: 'Document / lien', url: newMilestone.web_link }] : [],
      });
      toast.success('Jalon cree');
      setIsCreateMilestoneOpen(false);
      setNewMilestone({ project_id: '', name: '', description: '', target_date: new Date(), task_id: '', subtask_id: '', criticality: 'medium', reminder_value: 1, reminder_unit: 'days', web_link: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const openEditMilestone = (ms) => {
    setEditingMilestone({
      ...ms,
      target_date: new Date(ms.target_date),
      web_link: ms.web_links?.[0]?.url || '', reminder_value: ms.reminder_value || 1, reminder_unit: ms.reminder_unit || 'days', criticality: ms.criticality || 'medium', task_id: ms.task_id || '', subtask_id: ms.subtask_id || '',
    });
  };

  const handleUpdateMilestone = async () => {
    try {
      await milestonesApi.update(editingMilestone.id, {
        name: editingMilestone.name,
        description: editingMilestone.description,
        target_date: editingMilestone.target_date.toISOString(),
        task_id: editingMilestone.task_id || null, subtask_id: editingMilestone.subtask_id || null,
        criticality: editingMilestone.criticality || 'medium', reminder_value: Number(editingMilestone.reminder_value || 0) || null, reminder_unit: editingMilestone.reminder_unit || null,
        web_links: editingMilestone.web_link ? [{ label: 'Document / lien', url: editingMilestone.web_link }] : (editingMilestone.web_links || []),
        status: editingMilestone.status,
      });
      toast.success('Jalon mis a jour');
      setEditingMilestone(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const countWorkingDays = (startValue, endValue) => {
    const start = new Date(startValue); const end = new Date(endValue);
    let count = 0; const cursor = new Date(start);
    while (cursor <= end) { const d = cursor.getDay(); if (d !== 0 && d !== 6) count += 1; cursor.setDate(cursor.getDate() + 1); }
    return count;
  };

  const getSprintProgress = (sprint) => {
    const start = new Date(sprint.start_date);
    const end = new Date(sprint.end_date);
    const now = new Date();
    if (now < start) return 0;
    if (now > end) return 100;
    const total = differenceInDays(end, start);
    const elapsed = differenceInDays(now, start);
    return total > 0 ? Math.round((elapsed / total) * 100) : 0;
  };

  const getMilestoneStatus = (ms) => {
    if (ms.status === 'achieved') return 'achieved';
    if (isPast(new Date(ms.target_date))) return 'delayed';
    return ms.status || 'pending';
  };

  const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };

  // ========== SHARED FORM FIELDS ==========
  const SprintFormFields = ({ data, setData, isEdit = false }) => (
    <div className="space-y-3">
      {!isEdit && (
        <div className="space-y-1.5">
          <Label className="text-xs">Projet *</Label>
          <Select value={data.project_id} onValueChange={v => setData(p => ({ ...p, project_id: v, task_id: '', subtask_id: '' }))}>
            <SelectTrigger data-testid="sprint-project-select"><SelectValue placeholder="Selectionner un projet" /></SelectTrigger>
            <SelectContent>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.code} - {p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="space-y-1.5">
        <Label className="text-xs">Nom du sprint *</Label>
        <Input value={data.name} onChange={e => setData(p => ({ ...p, name: e.target.value }))} placeholder="Sprint 1" data-testid="sprint-name-input" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Description</Label>
        <Textarea value={data.description || ''} onChange={e => setData(p => ({ ...p, description: e.target.value }))} rows={2} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Debut</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-xs h-9">
                <CalendarBlank size={12} className="mr-1" />{format(data.start_date, 'dd/MM/yy', { locale: fr })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={data.start_date} onSelect={d => d && setData(p => ({ ...p, start_date: d }))} locale={fr} /></PopoverContent>
          </Popover>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Fin</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-xs h-9">
                <CalendarBlank size={12} className="mr-1" />{format(data.end_date, 'dd/MM/yy', { locale: fr })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={data.end_date} onSelect={d => d && setData(p => ({ ...p, end_date: d }))} locale={fr} /></PopoverContent>
          </Popover>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Capacite (jours)</Label>
          <Input type="number" step="0.5" value={data.capacity} onChange={e => setData(p => ({ ...p, capacity: e.target.value }))} placeholder="Ex: 10" />
        </div>
        <div className="rounded-lg border p-2 flex items-center gap-2">
          <Checkbox checked={data.working_days_only !== false} onCheckedChange={v => setData(p => ({ ...p, working_days_only: !!v }))} />
          <div><p className="text-xs font-medium">Jours ouvres uniquement</p><p className="text-[10px] text-slate-500">{countWorkingDays(data.start_date, data.end_date)} jours ouvrables sur la periode</p></div>
        </div>
        {isEdit && (
          <div className="space-y-1.5">
            <Label className="text-xs">Statut</Label>
            <Select value={data.status || 'planned'} onValueChange={v => setData(p => ({ ...p, status: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{SPRINT_STATUSES.map(x=><SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );

  const MilestoneFormFields = ({ data, setData, isEdit = false }) => (
    <div className="space-y-3">
      {!isEdit && (
        <div className="space-y-1.5">
          <Label className="text-xs">Projet *</Label>
          <Select value={data.project_id} onValueChange={v => setData(p => ({ ...p, project_id: v, task_id: '', subtask_id: '' }))}>
            <SelectTrigger data-testid="milestone-project-select"><SelectValue placeholder="Selectionner un projet" /></SelectTrigger>
            <SelectContent>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.code} - {p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="space-y-1.5">
        <Label className="text-xs">Nom du jalon *</Label>
        <Input value={data.name} onChange={e => setData(p => ({ ...p, name: e.target.value }))} placeholder="Fin de conception" data-testid="milestone-name-input" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Description</Label>
        <Textarea value={data.description || ''} onChange={e => setData(p => ({ ...p, description: e.target.value }))} rows={2} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Date cible *</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-xs h-9">
                <CalendarBlank size={12} className="mr-1" />{format(data.target_date, 'dd/MM/yy', { locale: fr })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={data.target_date} onSelect={d => d && setData(p => ({ ...p, target_date: d }))} locale={fr} /></PopoverContent>
          </Popover>
        </div>
        {isEdit && (
          <div className="space-y-1.5">
            <Label className="text-xs">Statut</Label>
            <Select value={data.status || 'pending'} onValueChange={v => setData(p => ({ ...p, status: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{MILESTONE_STATUSES.map(x=><SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Tache</Label><Select value={data.task_id || 'none'} onValueChange={v=>setData(p=>({...p,task_id:v==='none'?'':v,subtask_id:''}))}><SelectTrigger><SelectValue placeholder="Aucune"/></SelectTrigger><SelectContent><SelectItem value="none">Aucune</SelectItem>{milestoneTasks.filter(t=>!t.parent_task_id).map(t=><SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1.5"><Label className="text-xs">Sous-tache</Label><Select value={data.subtask_id || 'none'} onValueChange={v=>setData(p=>({...p,subtask_id:v==='none'?'':v}))} disabled={!data.task_id}><SelectTrigger><SelectValue placeholder="Aucune"/></SelectTrigger><SelectContent><SelectItem value="none">Aucune</SelectItem>{milestoneTasks.filter(t=>t.parent_task_id===data.task_id).map(t=><SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Criticite</Label><Select value={data.criticality || 'medium'} onValueChange={v=>setData(p=>({...p,criticality:v}))}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="low">Faible</SelectItem><SelectItem value="medium">Moyenne</SelectItem><SelectItem value="high">Haute</SelectItem><SelectItem value="critical">Critique</SelectItem></SelectContent></Select></div>
        <div className="space-y-1.5"><Label className="text-xs">Rappel</Label><Input type="number" min="1" value={data.reminder_value || ''} onChange={e=>setData(p=>({...p,reminder_value:e.target.value}))}/></div>
        <div className="space-y-1.5"><Label className="text-xs">Unite du rappel</Label><Select value={data.reminder_unit || 'days'} onValueChange={v=>setData(p=>({...p,reminder_unit:v}))}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="minutes">Minutes</SelectItem><SelectItem value="hours">Heures</SelectItem><SelectItem value="days">Jours</SelectItem><SelectItem value="weeks">Semaines</SelectItem><SelectItem value="months">Mois</SelectItem></SelectContent></Select></div>
      </div>
      <div className="space-y-1.5"><Label className="text-xs flex items-center gap-1"><LinkSimple size={13}/>Document Drive / OneDrive / SharePoint / URL</Label><Input value={data.web_link || ''} onChange={e=>setData(p=>({...p,web_link:e.target.value}))} placeholder="https://..."/></div>
    </div>
  );

  return (
    <div className="p-5 space-y-4" data-testid="sprints-milestones-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-slate-900">Sprints & Jalons</h1>
          <p className="text-xs text-slate-500">Planifiez vos iterations et jalons projet</p>
        </div>
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="w-48 h-8 text-xs"><SelectValue placeholder="Tous les projets" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les projets</SelectItem>
            {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.code} - {p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="sprints" className="space-y-4">
        <TabsList>
          <TabsTrigger value="sprints" className="flex items-center gap-1.5 text-xs">
            <Lightning size={14} /> Sprints ({sprints.length})
          </TabsTrigger>
          <TabsTrigger value="milestones" className="flex items-center gap-1.5 text-xs">
            <Flag size={14} /> Jalons ({milestones.length})
          </TabsTrigger>
        </TabsList>

        {/* ==================== SPRINTS TAB ==================== */}
        <TabsContent value="sprints" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={isCreateSprintOpen} onOpenChange={setIsCreateSprintOpen}>
              {hasPermission('sprints:create') && <DialogTrigger asChild>
                <Button className="btn-primary text-xs h-8" data-testid="create-sprint-btn"><Plus size={14} className="mr-1" />Nouveau sprint</Button>
              </DialogTrigger>}
              <DialogContent>
                <DialogHeader><DialogTitle>Creer un sprint</DialogTitle></DialogHeader>
                {SprintFormFields({ data:newSprint, setData:setNewSprint })}
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => setIsCreateSprintOpen(false)}>Annuler</Button>
                  <Button className="btn-primary" size="sm" onClick={handleCreateSprint} disabled={!newSprint.project_id || !newSprint.name} data-testid="submit-sprint-btn">Creer</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{[...Array(4)].map((_, i) => <div key={i} className="h-40 bg-slate-200 rounded-lg animate-pulse" />)}</div>
          ) : sprints.length === 0 ? (
            <Card className="card-surface"><CardContent className="py-12 text-center">
              <Lightning size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">Aucun sprint. Creez-en un pour commencer.</p>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sprints.map(sprint => {
                const progress = getSprintProgress(sprint);
                const project = projects.find(p => p.id === sprint.project_id);
                const isActive = sprint.status === 'active';
                return (
                  <Card key={sprint.id} className={`card-surface ${isActive ? 'ring-2 ring-blue-400' : ''}`} data-testid={`sprint-${sprint.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-[10px] font-mono text-slate-400">{project?.code}</p>
                          <h3 className="text-sm font-semibold text-slate-900">{sprint.name}</h3>
                        </div>
                        <div className="flex items-center gap-1">
                          <Badge className={getBadgeClass(sprint.status, 'status') + ' text-[10px]'}>{getLabel(sprint.status, 'status')}</Badge>
                          {hasPermission('sprints:update') && <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEditSprint(sprint)} data-testid={`edit-sprint-${sprint.id}`}>
                            <PencilSimple size={12} className="text-slate-500" />
                          </Button>}
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mb-3 line-clamp-2">{sprint.description || 'Pas de description'}</p>
                      <div className="space-y-2">
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-slate-400">Progression</span>
                            <span className="font-medium text-slate-600">{progress}%</span>
                          </div>
                          <Progress value={progress} className="h-1.5" />
                        </div>
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <div className="flex items-center gap-1">
                            <CalendarBlank size={12} />{formatDate(sprint.start_date)} - {formatDate(sprint.end_date)}
                          </div>
                          <span>{countWorkingDays(sprint.start_date, sprint.end_date)} j ouvrés{sprint.capacity ? ` · ${formatTimeHundredths(sprint.capacity)} cap.` : ''}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-slate-100">
                        <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => openSprintTasks(sprint)} data-testid={`sprint-tasks-${sprint.id}`}>
                          <ListChecks size={12} className="mr-1" />Taches
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => loadBurndown(sprint)} data-testid={`sprint-burndown-${sprint.id}`}>
                          <TrendingDown className="w-3 h-3 mr-1" />Burndown
                        </Button>
                        {hasPermission('sprints:update') && sprint.status === 'planned' && (
                          <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700" onClick={() => handleSprintStatus(sprint.id, 'active')}>
                            <Play size={12} className="mr-1" />Demarrer
                          </Button>
                        )}
                        {hasPermission('sprints:update') && sprint.status === 'active' && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleSprintStatus(sprint.id, 'completed')}>
                            <CheckCircle size={12} className="mr-1" />Cloturer
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* BURNDOWN CHART */}
          {burndownSprint && burndownData && (
            <Card className="card-surface mt-4" data-testid="burndown-chart">
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-blue-600" />
                  Burndown - {burndownData.sprint_name} ({burndownData.total_tasks} taches)
                </CardTitle>
                <Button size="sm" variant="ghost" onClick={() => { setBurndownSprint(null); setBurndownData(null); }}>
                  <X size={14} />
                </Button>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                {burndownData.chartData?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={burndownData.chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} label={{ value: 'Taches restantes', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="ideal" stroke="#94a3b8" strokeDasharray="8 4" strokeWidth={2} name="Ideal" dot={false} />
                      <Line type="monotone" dataKey="actual" stroke="#2563EB" strokeWidth={2.5} name="Reel" dot={{ r: 3 }} connectNulls={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">Aucune donnee disponible pour ce sprint</p>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ==================== MILESTONES TAB ==================== */}
        <TabsContent value="milestones" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={isCreateMilestoneOpen} onOpenChange={setIsCreateMilestoneOpen}>
              {hasPermission('milestones:create') && <DialogTrigger asChild>
                <Button className="bg-orange-600 hover:bg-orange-700 text-xs h-8" data-testid="create-milestone-btn"><Plus size={14} className="mr-1" />Nouveau jalon</Button>
              </DialogTrigger>}
              <DialogContent>
                <DialogHeader><DialogTitle>Creer un jalon</DialogTitle></DialogHeader>
                {MilestoneFormFields({ data:newMilestone, setData:setNewMilestone })}
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => setIsCreateMilestoneOpen(false)}>Annuler</Button>
                  <Button className="bg-orange-600 hover:bg-orange-700" size="sm" onClick={handleCreateMilestone} disabled={!newMilestone.project_id || !newMilestone.name} data-testid="submit-milestone-btn">Creer</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-slate-200 rounded-lg animate-pulse" />)}</div>
          ) : milestones.length === 0 ? (
            <Card className="card-surface"><CardContent className="py-12 text-center">
              <Flag size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">Aucun jalon. Creez des jalons pour marquer les etapes cles.</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-2">
              {milestones.map(ms => {
                const status = getMilestoneStatus(ms);
                const project = projects.find(p => p.id === ms.project_id);
                const daysUntil = differenceInDays(new Date(ms.target_date), new Date());
                return (
                  <Card key={ms.id} className={`card-surface ${status === 'delayed' ? 'border-red-200' : ''}`} data-testid={`milestone-${ms.id}`}>
                    <CardContent className="p-3 flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        status === 'achieved' ? 'bg-green-100' : status === 'delayed' ? 'bg-red-100' : 'bg-orange-100'
                      }`}>
                        {status === 'achieved' ? <CheckCircle size={18} className="text-green-600" /> :
                         status === 'delayed' ? <Warning size={18} className="text-red-600" /> :
                         <Flag size={18} className="text-orange-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-mono text-slate-400">{project?.code}</span>
                          <h4 className="text-sm font-medium text-slate-900 truncate">{ms.name}</h4>
                        </div>
                        <p className="text-xs text-slate-500 truncate">{ms.description || 'Pas de description'}</p><div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400">{ms.task_id && <span>Tache liee</span>}{ms.reminder_value && <span className="flex items-center gap-0.5"><Bell size={10}/>Rappel {ms.reminder_value} {ms.reminder_unit}</span>}{ms.web_links?.[0]?.url && <a href={ms.web_links[0].url} target="_blank" rel="noreferrer" className="text-blue-600 flex items-center gap-0.5"><LinkSimple size={10}/>Document</a>}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="flex items-center gap-1 text-xs">
                          <CalendarBlank size={12} className="text-slate-400" />
                          <span className={status === 'delayed' ? 'text-red-600 font-medium' : 'text-slate-600'}>{formatDate(ms.target_date)}</span>
                        </div>
                        <p className={`text-[10px] mt-0.5 ${status === 'achieved' ? 'text-green-600' : status === 'delayed' ? 'text-red-600' : daysUntil <= 7 ? 'text-orange-600' : 'text-slate-400'}`}>
                          {status === 'achieved' ? 'Atteint' : status === 'delayed' ? `Retard ${Math.abs(daysUntil)}j` : `Dans ${daysUntil}j`}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {hasPermission('milestones:update') && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditMilestone(ms)} data-testid={`edit-milestone-${ms.id}`}>
                          <PencilSimple size={14} className="text-slate-500" />
                        </Button>}
                        {hasPermission('milestones:update') && status !== 'achieved' && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { milestonesApi.update(ms.id, { status: 'achieved' }).then(() => { toast.success('Jalon atteint'); fetchData(); }); }}>
                            <CheckCircle size={12} className="mr-1" />Atteint
                          </Button>
                        )}
                        {hasPermission('milestones:delete') && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={async () => {
                            if (!window.confirm('Supprimer ce jalon ?')) return;
                            try { await milestonesApi.delete(ms.id); toast.success('Supprime'); fetchData(); } catch { toast.error('Erreur'); }
                          }} data-testid={`delete-milestone-${ms.id}`}>
                            <Trash size={14} />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ==================== EDIT SPRINT DIALOG ==================== */}
      <Dialog open={!!editingSprint} onOpenChange={() => setEditingSprint(null)}>
        <DialogContent data-testid="edit-sprint-dialog">
          <DialogHeader><DialogTitle>Modifier le sprint</DialogTitle></DialogHeader>
          {editingSprint && SprintFormFields({ data:editingSprint, setData:setEditingSprint, isEdit:true })}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setEditingSprint(null)}>Annuler</Button>
            <Button className="btn-primary" size="sm" onClick={handleUpdateSprint} disabled={!editingSprint?.name} data-testid="save-sprint-btn">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== EDIT MILESTONE DIALOG ==================== */}
      <Dialog open={!!editingMilestone} onOpenChange={() => setEditingMilestone(null)}>
        <DialogContent data-testid="edit-milestone-dialog">
          <DialogHeader><DialogTitle>Modifier le jalon</DialogTitle></DialogHeader>
          {editingMilestone && MilestoneFormFields({ data:editingMilestone, setData:setEditingMilestone, isEdit:true })}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setEditingMilestone(null)}>Annuler</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" size="sm" onClick={handleUpdateMilestone} disabled={!editingMilestone?.name} data-testid="save-milestone-btn">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== SPRINT TASKS DIALOG ==================== */}
      <Dialog open={!!viewingSprintTasks} onOpenChange={() => setViewingSprintTasks(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="sprint-tasks-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListChecks size={18} className="text-blue-600" />
              Taches - {viewingSprintTasks?.name}
            </DialogTitle>
          </DialogHeader>

          {/* Tasks in sprint */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-700">Taches dans le sprint ({sprintTasks.length})</p>
            {sprintTasks.length === 0 ? (
              <div className="py-6 text-center text-xs text-slate-400 bg-slate-50 rounded-lg">Aucune tache dans ce sprint</div>
            ) : (
              <div className="space-y-1">
                {sprintTasks.sort((a, b) => (priorityOrder[a.priority] ?? 9) - (priorityOrder[b.priority] ?? 9)).map(task => (
                  <div key={task.id} className="flex items-center gap-2 px-3 py-2 bg-white border rounded-lg hover:bg-slate-50" data-testid={`sprint-task-${task.id}`}>
                    <Badge className={getBadgeClass(task.status, 'status') + ' text-[9px]'}>{getLabel(task.status, 'status')}</Badge>
                    <Badge className={getBadgeClass(task.priority, 'priority') + ' text-[9px]'}>{getLabel(task.priority, 'priority')}</Badge>
                    <span className="flex-1 text-xs text-slate-900 truncate">{task.title}</span>
                    <span className="text-[10px] text-slate-400">{formatDate(task.end_date)}</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6 hover:bg-red-50" onClick={() => handleRemoveTaskFromSprint(task.id)} data-testid={`remove-task-${task.id}`}>
                      <X size={12} className="text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Available tasks */}
          <div className="space-y-2 mt-3 pt-3 border-t">
            <p className="text-xs font-semibold text-slate-700">Taches disponibles ({availableTasks.length})</p>
            {availableTasks.length === 0 ? (
              <div className="py-4 text-center text-xs text-slate-400 bg-slate-50 rounded-lg">Toutes les taches sont deja dans un sprint</div>
            ) : (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {availableTasks.map(task => (
                  <div key={task.id} className="flex items-center gap-2 px-3 py-2 bg-slate-50 border rounded-lg hover:bg-white" data-testid={`available-task-${task.id}`}>
                    <Badge className={getBadgeClass(task.status, 'status') + ' text-[9px]'}>{getLabel(task.status, 'status')}</Badge>
                    <Badge className={getBadgeClass(task.priority, 'priority') + ' text-[9px]'}>{getLabel(task.priority, 'priority')}</Badge>
                    <span className="flex-1 text-xs text-slate-700 truncate">{task.title}</span>
                    <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => handleAddTaskToSprint(task.id)} data-testid={`add-task-${task.id}`}>
                      <Plus size={10} className="mr-0.5" />Ajouter
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
